import React, { FC, useCallback, useEffect } from 'react';

import ANALYTICS_CATEGORIES from '../../analytics/categories';
import { Content } from '../../components/shared/Content';
import CreateAvtaleGiroForm from '../../components/transaction/AvtaleGiro/CreateAvtaleGiroForm';
import avtalegiroReducer from '../../components/transaction/AvtaleGiro/form/reducer';
import { avtalegiroInitialState } from '../../components/transaction/AvtaleGiro/form/state';
import { AvtalegiroFormContext } from '../../components/transaction/AvtaleGiro/form/useFormContext';
import validate from '../../components/transaction/AvtaleGiro/form/validate';
import useQueryParam from '../../hooks/queryparams/useQueryParam';
import usePaymentAccount from '../../hooks/usePaymentAccount';
import { SigningContextProvider, useSigningContext } from '../../signingContext/signingContext';
import useSetInitialAvtalegiroState from './useSetInitialAvtalegiroState';

import './CreateAvtaleGiro.scss';

export const getReturnUrl = (url: string, status: string) => {
    const splitUrl = decodeURIComponent(url).split('#');
    const [baseUrl, searchString = ''] = splitUrl[0].split('?');
    const queryParams = new URLSearchParams(searchString);
    queryParams.append('status', status);
    splitUrl[0] = `${baseUrl}?${queryParams.toString()}`;
    return splitUrl.join('#');
};

const CreateAvtaleGiro: FC = () => {
    const { signingStatus, signingError } = useSigningContext();

    const title = useQueryParam('title');
    const account = usePaymentAccount();
    const returnUrl = useQueryParam('returnUrl');

    const { kid, receiver } = useSetInitialAvtalegiroState();

    const getReturnStatus = useCallback(() => {
        if (signingError) return 'error';
        if (
            !signingStatus ||
            signingStatus === 'CANCELLED' ||
            signingStatus === 'CREATED' ||
            signingStatus === 'PROCESSING'
        )
            return 'cancel';
        if (signingStatus === 'COMPLETED') return 'ok';
        return 'error';
    }, [signingError, signingStatus]);

    const onCloseAvtaleGiro = useCallback(() => {
        if (signingStatus !== 'UNKNOWN') {
            if (returnUrl) {
                window.location.href = getReturnUrl(returnUrl, getReturnStatus());
            } else {
                window.history.back();
            }
        }
    }, [getReturnStatus, signingStatus, returnUrl]);

    useEffect(() => {
        if (signingStatus && signingStatus === 'COMPLETED') {
            setTimeout(onCloseAvtaleGiro, 2500);
        }
    }, [onCloseAvtaleGiro, signingStatus]);

    return (
        <Content className="create-avtalegiro-page">
            <CreateAvtaleGiroForm
                onClose={onCloseAvtaleGiro}
                account={account ?? undefined}
                title={title}
                lockKid={!!kid}
                lockReceiver={!!receiver}
                isInModal={false}
            />
        </Content>
    );
};

export default () => (
    <SigningContextProvider analyticsCategory={ANALYTICS_CATEGORIES.CREATE_AVTALEGIRO}>
        <AvtalegiroFormContext
            validateHook={validate}
            reducer={avtalegiroReducer}
            initialState={avtalegiroInitialState}
        >
            <CreateAvtaleGiro />
        </AvtalegiroFormContext>
    </SigningContextProvider>
);
